/**
 * ContactSection
 * Displays current phone and email (grayed out) alongside editable update fields.
 * Includes friendly hint copy explaining the update pattern.
 */
export default function ContactSection({ currentPhone, currentEmail, register, errors, isEmailRequired = false }) {
    const formatCurrentEmail = (email) => {
        if (!email) return 'No email found';
        if (email.length <= 28 || !email.includes('@')) return email;

        const [local, domain] = email.split('@');
        if (!local || !domain) return email;

        return (
            <>
                <div>{local}</div>
                <div>@{domain}</div>
            </>
        );
    };

    return (
        <div className="space-y-6">

            {/* Hint */}
            <div
                className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-xs leading-relaxed"
                style={{
                    background  : '#f0f8fd',
                    borderLeft  : '3px solid #2ECC8E',
                    borderRadius: '0 8px 8px 0',
                    color       : '#4a6882'
                }}
            >
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2ECC8E"
                    strokeWidth="2"
                    style={{ flexShrink: 0, marginTop: 1 }}
                    aria-hidden="true"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4m0 4h.01" />
                </svg>

                Your current contact information is shown below.
                If any details have changed, please enter your updated information.
            </div>

            {/* Phone + Email Blocks */}
            <div className="space-y-5">

                {/* PHONE */}
                <div className="space-y-3">

                    <div className="grid gap-1">

                        <label
                            className="text-xs font-semibold uppercase tracking-wide"
                            style={{ color: '#1B2F7E' }}
                        >
                            Current phone
                        </label>

                        <div
                            className="min-h-[20px] text-xs leading-relaxed"
                            style={{ color: '#4a6882' }}
                        >
                            {currentPhone || 'Not on file'}
                        </div>

                    </div>

                    <div className="grid gap-1">

                        <label
                            className="text-xs font-semibold uppercase tracking-wide"
                            style={{ color: '#1B2F7E' }}
                        >
                            Update phone
                        </label>

                        <input
                            type="tel"
                            inputMode="tel"
                            placeholder="New number…"
                            {...register('phone')}
                            className="w-full h-9 rounded-lg px-2.5 text-xs border outline-none"
                            style={{
                                borderColor: errors.phone ? '#E24B4A' : '#c5dff0',
                                background : 'white',
                                color      : '#1B2F7E',
                                fontStyle  : 'italic'
                            }}
                            onFocus={e => e.target.style.borderColor = '#2ECC8E'}
                            onBlur={e => e.target.style.borderColor = errors.phone ? '#E24B4A' : '#c5dff0'}
                            aria-label="Update phone number"
                        />

                        {errors.phone && (
                            <p
                                className="text-xs"
                                style={{ color: '#E24B4A' }}
                            >
                                {errors.phone.message}
                            </p>
                        )}

                    </div>

                </div>

                {/* Divider */}
                <div
                    style={{ borderTop: '0.5px solid #eaf4fb' }}
                />

                {/* EMAIL */}
                <div className="space-y-3">

                    <div className="grid gap-1">

                        <label
                            className="text-xs font-semibold uppercase tracking-wide"
                            style={{ color: '#1B2F7E' }}
                        >
                            Current email
                        </label>

                        <div
                            className="min-h-[20px] text-xs leading-relaxed break-words"
                            style={{ color: '#4a6882' }}
                            title={currentEmail || 'No email found'}
                        >
                            {formatCurrentEmail(currentEmail)}
                        </div>

                    </div>

                    <div className="grid gap-1">

                        <label
                            className="text-xs font-semibold uppercase tracking-wide"
                            style={{ color: '#1B2F7E' }}
                        >
                            Update email
                            {isEmailRequired && (
                                <span aria-hidden="true" style={{ color: '#E24B4A' }}> *</span>
                            )}
                        </label>

                        <input
                            type="email"
                            inputMode="email"
                            placeholder="New email…"
                            {...register('email')}
                            required={isEmailRequired}
                            aria-required={isEmailRequired}
                            className="w-full h-9 rounded-lg px-2.5 text-xs border outline-none"
                            style={{
                                borderColor: errors.email ? '#E24B4A' : '#c5dff0',
                                background : 'white',
                                color      : '#1B2F7E',
                                fontStyle  : 'italic'
                            }}
                            onFocus={e => e.target.style.borderColor = '#2ECC8E'}
                            onBlur={e => e.target.style.borderColor = errors.email ? '#E24B4A' : '#c5dff0'}
                            aria-label="Update email address"
                        />

                        {errors.email && (
                            <p
                                className="text-xs"
                                style={{ color: '#E24B4A' }}
                            >
                                {errors.email.message}
                            </p>
                        )}

                    </div>

                </div>

            </div>

        </div>
    );
}